import './PackageItemShow.scss';

import { TextField, useForm } from '@refinedev/antd';
import { useResourceParams } from '@refinedev/core';
import { Button, Form, Input, Modal } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PRODUCT_TYPES } from '@/model/Logistic/PackageItem/ProductTypes';
import { RESOURCES } from '@/Resources';

import ReceptionPackageItemShow from './ReceptionPackageItemShow';
import ShipmentPackageItemShow from './ShipmentPackageItemShow';

interface PackageItemShowProps {
  packageItems: PackageItem[];
  closeModal: () => void;
  editingPackageItem: PackageItem;
  editEnabled: boolean;
  refetchPackageItems: () => void;
  canEditResolution?: boolean;
}

const PackageItemShow = (props: PackageItemShowProps) => {
  const { t } = useTranslation();
  const { resource } = useResourceParams();
  const {
    packageItems,
    closeModal,
    editingPackageItem,
    editEnabled,
    refetchPackageItems,
    canEditResolution,
  } = props;
  const { comment } = editingPackageItem;
  const { formProps } = useForm<PackageItem>({
    resource: 'packages_items',
    id: editingPackageItem.id,
    action: 'edit',
    queryOptions: {
      enabled: false,
    },
    successNotification: {
      message: 'Item editado con éxito',
      type: 'success',
    },
    onMutationSuccess: () => {
      refetchPackageItems();
      closeModal?.();
    },
  });
  const onFinish = useCallback(() => {
    const values = formProps?.form?.getFieldsValue() as PackageItem;
    const newPackageItem: PackageItem = {
      ...values,
      name: formProps?.form?.getFieldValue('name'),
    };
    const handleItemUpdate = () => {
      if (editingPackageItem?.id) {
        newPackageItem.id = editingPackageItem.id;
        newPackageItem.toUpdate = true;
      }

      formProps.onFinish?.(values);
    };

    const handleExistingItem = () => {
      Modal.error({
        title: 'Elemento existente',
        content: 'Ya existe un item que contiene este elemento.',
      });

      return;
    };

    const itemWithSameProduct = packageItems.find(item => {
      if (item !== editingPackageItem && !item.toDelete) {
        if (item.product_type == PRODUCT_TYPES.PRODUCT) {
          return (
            item.product === newPackageItem.product &&
            item.item_shipped_reference === newPackageItem.item_shipped_reference
          );
        } else if (item.product_type == PRODUCT_TYPES.UNIT) {
          return (
            item.unidad != undefined &&
            newPackageItem.unidad != undefined &&
            item.unidad === newPackageItem.unidad &&
            item.item_shipped_reference === newPackageItem.item_shipped_reference
          );
        } else if (item.product_type == PRODUCT_TYPES.DEVICE) {
          return (
            item.device != undefined &&
            newPackageItem.device != undefined &&
            item.device === newPackageItem.device &&
            item.item_shipped_reference === newPackageItem.item_shipped_reference
          );
        }
      }
    });

    if (editingPackageItem) {
      const hasChanges = Object.keys(newPackageItem).some(
        key =>
          editingPackageItem[key as keyof PackageItem] !== newPackageItem[key as keyof PackageItem],
      );

      if (!hasChanges) {
        closeModal?.();

        return;
      }

      if (itemWithSameProduct) {
        Modal.error({
          title: 'Elemento existente',
          content: 'Ya existe un item que contiene este elemento.',
        });

        return;
      }

      handleItemUpdate();
    } else if (itemWithSameProduct) {
      handleExistingItem();
    }
  }, [formProps, packageItems, editingPackageItem, closeModal]);
  const showShipmentData = useMemo(
    () =>
      resource?.name == RESOURCES.SHIPMENTS || resource?.name == RESOURCES.SPARE_PARTS_COLLECTIONS,
    [resource],
  );
  const showReceptionData = useMemo(
    () => resource?.name == RESOURCES.RECEPTIONS || resource?.name == RESOURCES.CASE_RESOLUTION,
    [resource],
  );

  return (
    <Form
      {...formProps}
      initialValues={editingPackageItem}
      className="PackageItemShow"
      onFinish={() => onFinish()}>
      <div className="PackageItemShowContent">
        {showShipmentData && (
          <ShipmentPackageItemShow
            formProps={formProps}
            editingPackageItem={editingPackageItem}
            editEnabled={editEnabled}
          />
        )}
        {showReceptionData && (
          <ReceptionPackageItemShow
            formProps={formProps}
            editEnabled={editEnabled}
            editingPackageItem={editingPackageItem}
            canEditResolution={canEditResolution}
          />
        )}

        <Form.Item label={t('Observaciones')} name={editEnabled ? 'comment' : undefined}>
          {editEnabled ? <Input /> : <TextField value={comment ?? '-'} />}
        </Form.Item>
      </div>
      <div className="PackageItemShowButtons">
        <Button
          type={editEnabled || canEditResolution ? 'default' : 'primary'}
          onClick={closeModal}>
          {editEnabled ? t('Cancelar') : t('Cerrar')}
        </Button>
        {(editEnabled || canEditResolution) && (
          <Button type={'primary'} htmlType="submit">
            {t('Actualizar')}
          </Button>
        )}
      </div>
    </Form>
  );
};

export default PackageItemShow;
